import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

export type AttentionTone = 'critical' | 'warn' | 'ready' | 'suggestion'

export type NavAttention = {
  home:     'critical' | 'warn' | null
  shop:     'ready' | null
  recipes:  null
  spending: null
  more:     null
}

export function useNavAttention(): NavAttention {
  const { user } = useAuth()

  const { data: householdId } = useQuery({
    queryKey:  ['householdId', user?.id],
    enabled:   !!user?.id,
    staleTime: 60_000,
    queryFn:   async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', user!.id)
        .maybeSingle()
      return (data?.household_id as string) ?? null
    },
  })

  const { data: attentionData } = useQuery({
    queryKey:  ['nav-attention', householdId],
    enabled:   !!householdId,
    staleTime: 30_000,
    queryFn:   async () => {
      const today  = new Date()
      const in7    = new Date(today); in7.setDate(in7.getDate() + 7)
      const past2  = new Date(today); past2.setDate(past2.getDate() - 2)
      const in7Str   = in7.toISOString().split('T')[0]
      const past2Str = past2.toISOString().split('T')[0]

      const [itemsRes, listRes] = await Promise.all([
        supabase
          .from('pantry_items')
          .select('status_override, track_expiration, current_expiration_date')
          .eq('household_id', householdId!)
          .eq('is_active', true),
        supabase
          .from('shopping_lists')
          .select('id')
          .eq('household_id', householdId!)
          .is('completed_at', null)
          .limit(1)
          .maybeSingle(),
      ])

      const items           = itemsRes.data ?? []
      const outCount        = items.filter(i => i.status_override === 'out').length
      const expiringSoonCount = items.filter(i => {
        if (!i.track_expiration || !i.current_expiration_date) return false
        return i.current_expiration_date >= past2Str && i.current_expiration_date <= in7Str
      }).length

      return { outCount, expiringSoonCount, hasUnshoppedList: !!listRes.data }
    },
  })

  return useMemo((): NavAttention => {
    if (!attentionData) return { home: null, shop: null, recipes: null, spending: null, more: null }

    let home: NavAttention['home'] = null
    if (attentionData.outCount > 0) home = 'critical'
    else if (attentionData.expiringSoonCount >= 3) home = 'warn'

    const shop: NavAttention['shop'] = attentionData.hasUnshoppedList ? 'ready' : null

    // TODO: recipes dot — implement when recipe-suggestion logic is formalized
    return { home, shop, recipes: null, spending: null, more: null }
  }, [attentionData])
}
